import { createPlace, updatePlace } from '../domain/place';
import type { Place, PlaceChanges, PlaceInput } from '../domain/types';
import {
  PlaceNotFoundError,
  type AppSnapshot,
  type PlacesRepository,
} from './repository';

const DEFAULT_DATABASE_NAME = 'mesta-moscow';
const DATABASE_VERSION = 1;

const PLACES_STORE = 'places';
const SETTINGS_STORE = 'settings';

const FAVORITE_STATIONS_KEY = 'favoriteStationIds';
const CUSTOM_TAGS_KEY = 'customTags';

type SettingsKey =
  | typeof FAVORITE_STATIONS_KEY
  | typeof CUSTOM_TAGS_KEY;

interface StoredSetting {
  key: SettingsKey;
  value: unknown;
}

export interface IndexedDbRepositoryOptions {
  /** Useful for isolated integration tests and future migrations. */
  databaseName?: string;
  indexedDBFactory?: IDBFactory;
  createId?: () => string;
  now?: () => string;
}

export class IndexedDbRepositoryError extends Error {
  constructor(operation: string, cause: unknown) {
    super(`Не удалось выполнить операцию с локальными данными: ${operation}.`, {
      cause,
    });
    this.name = 'IndexedDbRepositoryError';
  }
}

/** Native IndexedDB adapter. It has no dependency on React or browser UI. */
export class IndexedDbRepository implements PlacesRepository {
  private readonly databaseName: string;
  private readonly indexedDBFactory: IDBFactory;
  private readonly createId: () => string;
  private readonly now: () => string;
  private databasePromise: Promise<IDBDatabase> | undefined;

  constructor(options: IndexedDbRepositoryOptions = {}) {
    const indexedDBFactory = options.indexedDBFactory ?? globalThis.indexedDB;

    if (!indexedDBFactory) {
      throw new IndexedDbRepositoryError(
        'инициализация',
        new Error('IndexedDB не поддерживается этим браузером'),
      );
    }

    this.databaseName = options.databaseName ?? DEFAULT_DATABASE_NAME;
    this.indexedDBFactory = indexedDBFactory;
    this.createId = options.createId ?? createRandomId;
    this.now = options.now ?? (() => new Date().toISOString());
  }

  async load(): Promise<AppSnapshot> {
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(
        [PLACES_STORE, SETTINGS_STORE],
        'readonly',
      );
      const placesStore = transaction.objectStore(PLACES_STORE);
      const settingsStore = transaction.objectStore(SETTINGS_STORE);

      return await finishTransaction(transaction, async () => {
        const [places, favoriteStations, customTags] = await Promise.all([
          requestResult(placesStore.getAll() as IDBRequest<Place[]>),
          requestResult(
            settingsStore.get(FAVORITE_STATIONS_KEY) as IDBRequest<
              StoredSetting | undefined
            >,
          ),
          requestResult(
            settingsStore.get(CUSTOM_TAGS_KEY) as IDBRequest<
              StoredSetting | undefined
            >,
          ),
        ]);

        return {
          places,
          favoriteStationIds: normalizeStringList(favoriteStations?.value),
          customTags: normalizeStringList(customTags?.value),
        };
      });
    } catch (error) {
      throw asRepositoryError('загрузка', error);
    }
  }

  async createPlace(input: PlaceInput): Promise<Place> {
    const place = createPlace(input, {
      id: this.createId(),
      now: this.now(),
    });

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PLACES_STORE, 'readwrite');
      const request = transaction.objectStore(PLACES_STORE).add(place);

      await finishTransaction(transaction, () => requestResult(request));
      return clonePlace(place);
    } catch (error) {
      throw asRepositoryError('создание места', error);
    }
  }

  async updatePlace(id: string, changes: PlaceChanges): Promise<Place> {
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PLACES_STORE, 'readwrite');
      const store = transaction.objectStore(PLACES_STORE);

      return await finishTransaction(transaction, async () => {
        const current = await requestResult(
          store.get(id) as IDBRequest<Place | undefined>,
        );

        if (!current) {
          throw new PlaceNotFoundError(id);
        }

        const updated = updatePlace(current, changes, { now: this.now() });
        await requestResult(store.put(updated));
        return clonePlace(updated);
      });
    } catch (error) {
      throw asRepositoryError('изменение места', error);
    }
  }

  async deletePlace(id: string): Promise<void> {
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PLACES_STORE, 'readwrite');
      const request = transaction.objectStore(PLACES_STORE).delete(id);
      await finishTransaction(transaction, () => requestResult(request));
    } catch (error) {
      throw asRepositoryError('удаление места', error);
    }
  }

  async setStationFavorite(
    stationId: string,
    favorite: boolean,
  ): Promise<void> {
    const normalizedStationId = stationId.trim();
    if (!normalizedStationId) {
      throw new TypeError('Идентификатор станции не может быть пустым.');
    }

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(SETTINGS_STORE, 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);

      await finishTransaction(transaction, async () => {
        const stored = await requestResult(
          store.get(FAVORITE_STATIONS_KEY) as IDBRequest<
            StoredSetting | undefined
          >,
        );
        const stationIds = normalizeStringList(stored?.value);
        const nextStationIds = favorite
          ? mergeUniqueStrings(stationIds, [normalizedStationId])
          : stationIds.filter((item) => item !== normalizedStationId);

        await requestResult(
          store.put({
            key: FAVORITE_STATIONS_KEY,
            value: nextStationIds,
          } satisfies StoredSetting),
        );
      });
    } catch (error) {
      throw asRepositoryError('изменение избранных станций', error);
    }
  }

  async rememberTags(tags: readonly string[]): Promise<void> {
    const normalizedTags = normalizeStringList(tags);
    if (normalizedTags.length === 0) return;

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(SETTINGS_STORE, 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);

      await finishTransaction(transaction, async () => {
        const stored = await requestResult(
          store.get(CUSTOM_TAGS_KEY) as IDBRequest<StoredSetting | undefined>,
        );
        const nextTags = mergeUniqueStrings(
          normalizeStringList(stored?.value),
          normalizedTags,
        );

        await requestResult(
          store.put({
            key: CUSTOM_TAGS_KEY,
            value: nextTags,
          } satisfies StoredSetting),
        );
      });
    } catch (error) {
      throw asRepositoryError('сохранение тегов', error);
    }
  }

  async replaceAll(snapshot: AppSnapshot): Promise<void> {
    const safeSnapshot = cloneSnapshot(snapshot);

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(
        [PLACES_STORE, SETTINGS_STORE],
        'readwrite',
      );
      const placesStore = transaction.objectStore(PLACES_STORE);
      const settingsStore = transaction.objectStore(SETTINGS_STORE);

      await finishTransaction(transaction, async () => {
        // Queue every request synchronously. This avoids Safari closing an
        // otherwise idle transaction between two awaited promise batches.
        const requests = [
          requestResult(placesStore.clear()),
          requestResult(settingsStore.clear()),
          ...safeSnapshot.places.map((place) =>
            requestResult(placesStore.add(place)),
          ),
          requestResult(
            settingsStore.put({
              key: FAVORITE_STATIONS_KEY,
              value: safeSnapshot.favoriteStationIds,
            } satisfies StoredSetting),
          ),
          requestResult(
            settingsStore.put({
              key: CUSTOM_TAGS_KEY,
              value: safeSnapshot.customTags,
            } satisfies StoredSetting),
          ),
        ];

        // add() rejects duplicate ids and rolls the whole import back.
        await Promise.all(requests);
      });
    } catch (error) {
      throw asRepositoryError('восстановление резервной копии', error);
    }
  }

  /** Primarily useful for tests or a future database-version handover. */
  async close(): Promise<void> {
    const databasePromise = this.databasePromise;
    this.databasePromise = undefined;
    if (!databasePromise) return;

    try {
      const database = await databasePromise;
      database.close();
    } catch {
      // A failed open has no database handle to close.
    }
  }

  private getDatabase(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;

    const databasePromise = openDatabase(
      this.indexedDBFactory,
      this.databaseName,
      () => {
        if (this.databasePromise === databasePromise) {
          this.databasePromise = undefined;
        }
      },
    );

    this.databasePromise = databasePromise;
    void databasePromise.catch(() => {
      if (this.databasePromise === databasePromise) {
        this.databasePromise = undefined;
      }
    });

    return databasePromise;
  }
}

export function createIndexedDbRepository(
  options: IndexedDbRepositoryOptions = {},
): PlacesRepository {
  return new IndexedDbRepository(options);
}

function openDatabase(
  indexedDBFactory: IDBFactory,
  databaseName: string,
  onVersionChange: () => void,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDBFactory.open(databaseName, DATABASE_VERSION);
    let blocked = false;

    request.addEventListener('upgradeneeded', () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(PLACES_STORE)) {
        database.createObjectStore(PLACES_STORE, { keyPath: 'id' });
      }

      if (!database.objectStoreNames.contains(SETTINGS_STORE)) {
        database.createObjectStore(SETTINGS_STORE, { keyPath: 'key' });
      }
    });

    request.addEventListener('success', () => {
      const database = request.result;
      if (blocked) {
        database.close();
        return;
      }
      database.addEventListener('versionchange', () => {
        database.close();
        onVersionChange();
      });
      resolve(database);
    });

    request.addEventListener('error', () => {
      reject(request.error ?? new Error('Не удалось открыть IndexedDB.'));
    });

    request.addEventListener(
      'blocked',
      () => {
        blocked = true;
        reject(
          new Error(
            'Открытие IndexedDB заблокировано другой вкладкой приложения.',
          ),
        );
      },
      { once: true },
    );
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result), {
      once: true,
    });
    request.addEventListener(
      'error',
      () => reject(request.error ?? new Error('Ошибка запроса IndexedDB.')),
      { once: true },
    );
  });
}

function transactionCompleted(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener('complete', () => resolve(), { once: true });
    transaction.addEventListener(
      'abort',
      () =>
        reject(
          transaction.error ?? new Error('Транзакция IndexedDB отменена.'),
        ),
      { once: true },
    );
    transaction.addEventListener(
      'error',
      () =>
        reject(
          transaction.error ?? new Error('Ошибка транзакции IndexedDB.'),
        ),
      { once: true },
    );
  });
}

async function finishTransaction<T>(
  transaction: IDBTransaction,
  operation: () => Promise<T>,
): Promise<T> {
  // Register completion listeners before the first request can finish.
  const completed = transactionCompleted(transaction);

  try {
    const result = await operation();
    await completed;
    return result;
  } catch (error) {
    abortIfActive(transaction);
    await completed.catch(() => undefined);
    throw error;
  }
}

function abortIfActive(transaction: IDBTransaction): void {
  try {
    transaction.abort();
  } catch {
    // The transaction has already completed or was already aborted.
  }
}

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  const normalized: string[] = [];
  const seen = new Set<string>();

  for (const candidate of value) {
    if (typeof candidate !== 'string') continue;
    const label = candidate.trim().replace(/\s+/gu, ' ');
    if (!label) continue;
    const key = comparableString(label);
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(label);
  }

  return normalized;
}

function mergeUniqueStrings(
  current: readonly string[],
  additions: readonly string[],
): string[] {
  return normalizeStringList([...current, ...additions]);
}

function comparableString(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase('ru-RU')
    .replaceAll('ё', 'е');
}

function clonePlace(place: Place): Place {
  return { ...place, tags: [...place.tags] };
}

function cloneSnapshot(snapshot: AppSnapshot): AppSnapshot {
  return {
    places: snapshot.places.map(clonePlace),
    favoriteStationIds: normalizeStringList(snapshot.favoriteStationIds),
    customTags: normalizeStringList(snapshot.customTags),
  };
}

function createRandomId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  // IndexedDB is normally available in environments with Web Crypto, but
  // retain a low-collision fallback for older embedded browsers.
  return `place-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

function asRepositoryError(
  operation: string,
  error: unknown,
): IndexedDbRepositoryError | PlaceNotFoundError {
  if (error instanceof PlaceNotFoundError) return error;
  if (error instanceof IndexedDbRepositoryError) return error;
  return new IndexedDbRepositoryError(operation, error);
}
